'use client';

import clsx from 'clsx';
import { type ChangeEvent, type ComponentProps, type ComponentType, forwardRef, type ReactNode } from 'react';
import ReactMaskedInput, { type MaskedInputProps } from 'react-text-mask';

import { useBoolean } from '../../../../hooks/use-boolean';
import { useCombineRefs } from '../../../../hooks/use-combine-refs';
import { useElementSize } from '../../../../hooks/use-element-size';
import { getCssSizeValue } from '../../../../utils/css';
import { isDefined } from '../../../../utils/is';
import { EyeV4Icon } from '../../../icons/EyeV4Icon';
import { ControlState } from '../Control';

import styles from './TextInput.module.scss';

export type TextInputProps = {
  variant?: 'default' | 'box';
  type?: ComponentProps<'input'>['type'];
  rows?: ComponentProps<'textarea'>['rows'];
  prefix?: ReactNode;
  suffix?: ReactNode;
  state?: ControlState;
  mask?: MaskedInputProps['mask'];
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => unknown;
} & (
  | (Omit<ComponentProps<'input'>, 'ref' | 'onChange'> & {
      multiline?: false;
    })
  | (Omit<ComponentProps<'textarea'>, 'ref' | 'onChange'> & {
      multiline: true;
    })
);

const MaskedInput = ReactMaskedInput as ComponentType<MaskedInputProps>;

export const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>((props, ref) => {
  const {
    className,
    value,
    type,
    multiline,
    variant = 'default',
    prefix,
    suffix,
    state = 'default',
    mask,
    onChange,
    ...inputProps
  } = props;

  const { value: openedPassword, toggle: toggleOpenedPassword } = useBoolean(false);

  const inputElementProps = {
    className: styles.input,
    type: type === 'password' && openedPassword ? 'text' : type,
    value: value ?? '',
    ...inputProps,
  };

  const suffixNode =
    suffix ?? (type === 'password' ? <EyeV4Icon onClick={toggleOpenedPassword} /> : inputProps.required ? '*' : null);

  const { size: prefixSize, ref: prefixRef } = useElementSize();
  const { size: suffixSize, ref: suffixRef } = useElementSize();

  const combinedInputRef = useCombineRefs([ref]);

  const Element = multiline ? 'textarea' : 'input';

  return (
    <div
      className={clsx(
        styles.root,
        styles[`variant_${variant}`],
        styles[`state_${state}`],
        {
          [styles.withPrefix]: prefix,
          [styles.withSuffix]: suffixNode,
          [styles.multiline]: multiline,
        },
        className
      )}
      style={{
        '--text-input-prefix-width': getCssSizeValue(prefixSize.width),
        '--text-input-suffix-width': getCssSizeValue(suffixSize.width),
      }}
    >
      {mask ? (
        <MaskedInput
          mask={mask}
          onChange={onChange}
          render={(ref, restProps) => (
            <Element
              ref={(input: HTMLInputElement | HTMLTextAreaElement | null) => {
                combinedInputRef(input);

                if (input) {
                  ref(input);
                }
              }}
              {...restProps}
              {...(inputElementProps as any)}
            />
          )}
        />
      ) : (
        <Element ref={combinedInputRef} onChange={onChange} {...(inputElementProps as any)} />
      )}

      {isDefined(prefix) && (
        <div className={styles.prefix} ref={prefixRef}>
          {prefix}
        </div>
      )}

      {isDefined(suffixNode) && (
        <div className={styles.suffix} ref={suffixRef}>
          {suffixNode}
        </div>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';
